import api from './api';

/**
 * BXGY "get" (Y) item helpers shared by the Add-offer-item picker (BxgyAddYModal) and the
 * single-Y auto-add on coupon apply (cartStore.applyDiscount) — Oct 2026.
 *
 * `data` is the GET /cart/bxgy-options/:code response: { locationId, locations, yQty, products[{ variants[{
 * stockByLocation, inventoryQuantity, stockKnown, inventoryPolicy, ... }] }] }.
 */

export const idTail = (id) => { const m = String(id == null ? '' : id).match(/(\d+)\s*$/); return m ? m[1] : String(id); };
export const isDefaultVariant = (v) => !v.title || v.title === 'Default Title';

// Same dimension parsing as ProductSearch, for the MTO "_custom_size" attribute.
const parseDims = (title) => {
  const t = String(title || '');
  const m3 = t.match(/(\d+)\s*x\s*(\d+)\s*x\s*(\d+)/);
  if (m3) return { length: parseInt(m3[1], 10), width: parseInt(m3[2], 10), height: parseInt(m3[3], 10) };
  const m2 = t.match(/(\d+)\s*x\s*(\d+)/);
  if (m2) return { length: parseInt(m2[1], 10), width: parseInt(m2[2], 10), height: null };
  return null;
};

// Stock / location rules (same as product search): a store sells only in-stock units; a warehouse also
// sells made-to-order (0 stock + continue-selling). `fallback` = { id, name, type } of the selected
// location, used when the server sent no location list.
export function bxgyStockRules(data, fallback = {}) {
  const curId = data?.locationId != null ? String(data.locationId) : (fallback.id != null ? String(fallback.id) : '');
  const locations = (data?.locations && data.locations.length) ? data.locations
    : [{ id: curId, name: fallback.name || '', type: fallback.type || data?.locationType || 'store', current: true }];
  const locById = (id) => locations.find(l => String(l.id) === String(id)) || null;
  const stockAt = (v, l) => {
    if (!l) return { stock: 0, known: true };
    const s = (v.stockByLocation || {})[l.id];
    if (s) return s;
    return String(l.id) === curId ? { stock: Number(v.inventoryQuantity || 0), known: v.stockKnown !== false } : { stock: 0, known: true };
  };
  const mtoAt = (v, l) => !!l && l.type !== 'store' && v.inventoryPolicy === 'continue' && stockAt(v, l).stock <= 0;
  const usableAt = (v, l) => !!l && ((stockAt(v, l).known && stockAt(v, l).stock > 0) || mtoAt(v, l));
  // Where a new unit comes from: the selected location if it can sell it, else a warehouse that can.
  const defaultSource = (v) => {
    const cur = locById(curId);
    if (usableAt(v, cur)) return cur;
    return locations.find(l => l.type === 'warehouse' && String(l.id) !== curId && usableAt(v, l)) || null;
  };
  return { curId, locations, locById, stockAt, mtoAt, usableAt, defaultSource };
}

// MTO line attributes — the same rules product search applies at a warehouse.
export async function bxgyMtoAttributes(product, variant, isMto) {
  if (!isMto) return undefined;
  const t = (product.title || '').toLowerCase();
  if (t.includes('dog bed cover') || t.includes('mattress cover')) {
    const m = String(variant.title || '').trim().match(/([A-Za-z][A-Za-z ]*?)\s*[-/]\s*(\d+\s*x\s*\d+(?:\s*x\s*\d+)?)\s*$/);
    return [{ key: '_custom_size', value: m ? `${m[1].trim()} - ${m[2].trim()}` : String(variant.title || '').trim() }];
  }
  try {
    const r = await api.get(`/products/variant/${variant.id}/is-mto`);
    if (!r.data.isMto) return undefined;
    const d = parseDims(variant.title);
    return [
      { key: '_is_mto', value: '1' },
      ...(d ? [{ key: '_custom_size', value: `${d.length} x ${d.width}${d.height ? ' x ' + d.height : ''}_ismto` }] : [])
    ];
  } catch (e) { return undefined; }
}

// The POST /cart/add body for `qty` units of a Y variant taken from location `src`.
export function bxgyCartItem(product, variant, src, qty, stock, curName, customAttributes) {
  return {
    variantId: variant.id,
    productId: product.productId,
    title: `${product.title}${isDefaultVariant(variant) ? '' : ' - ' + variant.title}`,
    price: variant.price,
    compareAtPrice: variant.compareAtPrice || null,
    quantity: qty,
    sku: variant.sku,
    image: variant.imageSrc || product.image || null,
    inventoryQuantity: stock,
    inventoryPolicy: variant.inventoryPolicy || null,
    inventoryItemId: variant.inventoryItemId || null,
    locationType: src.type || 'store',
    locationId: src.id || null,
    locationName: src.name || curName || '',
    ...(customAttributes ? { customAttributes } : {})
  };
}

/**
 * Single-Y auto-add: when a BXGY offer's Y list is exactly ONE variant, applying the code adds that
 * variant for the units still missing (no picker needed). Called right after the code is applied.
 * Returns { added: [{ variantId, locationId, qty, customSize, title }], note } — `note` explains why
 * nothing (or fewer units) was added, e.g. out of stock everywhere.
 */
export async function autoAddSingleBxgyY({ code, needQty, locationId, addToCart }) {
  if (!(needQty > 0)) return { added: [] };
  let data;
  try { data = (await api.get(`/cart/bxgy-options/${encodeURIComponent(code)}`, { params: { locationId } })).data; }
  catch (e) { return { added: [] }; }
  const all = (data.products || []).flatMap(p => p.variants.map(v => ({ product: p, variant: v })));
  if (all.length !== 1) return { added: [] }; // several choices → staff picks in the "Add offer item" picker
  const { product, variant } = all[0];
  const name = `${product.title}${isDefaultVariant(variant) ? '' : ' - ' + variant.title}`;
  const rules = bxgyStockRules(data, { id: locationId });
  const src = rules.defaultSource(variant);
  if (!src) return { added: [], note: `${name} is out of stock — add it when available` };
  const mto = rules.mtoAt(variant, src);
  const stock = rules.stockAt(variant, src).stock;
  const qty = mto ? needQty : Math.min(needQty, stock);
  const customAttributes = await bxgyMtoAttributes(product, variant, mto);
  const r = await addToCart(bxgyCartItem(product, variant, src, qty, stock, '', customAttributes));
  if (!r.success) return { added: [], note: `Couldn't add ${name}: ${r.error || 'error'}` };
  const customSize = (customAttributes || []).find(a => a.key === '_custom_size')?.value || null;
  return {
    added: [{ variantId: idTail(variant.id), locationId: src.id != null ? String(src.id) : '', qty, customSize, title: name }],
    note: qty < needQty ? `Only ${qty} of ${needQty} ${name} in stock` : ''
  };
}
